import { Card } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { BatchCostOverview, RunCost } from "@/types";
import { compactTime, formatUsd } from "@/utils/format";
import { costTitle, formatCost } from "./cost";

const dollars = (value: number) => `$${value.toFixed(2)}`;

/**
 * The Batch Test list's cost header: what every batch run cost, and the
 * OpenRouter account's spend since the last top-up ("$70 / $100"). The
 * account is shared with product chat and dev, so the meter is not batches alone.
 */
export function CostOverview({ data, error }: { data: BatchCostOverview | null; error?: string }) {
  const meter = data?.openrouter;
  const batches = data?.batches;
  const total: RunCost | null =
    batches && batches.usd != null
      ? { usd: batches.usd, replay_usd: batches.replay_usd, judge_usd: batches.judge_usd,
          events: batches.events ?? 0, unpriced: batches.unpriced ?? 0, unreported: batches.unreported ?? 0 }
      : null;
  const ratio = meter && !("error" in meter) && meter.budget_usd > 0 ? meter.used_usd / meter.budget_usd : null;

  return (
    <Card className="grid gap-4 p-4 md:grid-cols-2">
      <div className="grid content-start gap-1">
        <span className="text-[11px] text-muted-foreground">Batch 총 비용</span>
        <span className="text-2xl font-semibold tabular-nums" title={costTitle(total)}>
          {error ? "—" : data == null ? "…" : formatCost(total)}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {batches ? `실행 ${batches.runs} 회 · 실행 ${formatUsd(batches.replay_usd ?? 0)} + 채점 ${formatUsd(batches.judge_usd ?? 0)}` : error || " "}
        </span>
      </div>
      <div className="grid content-start gap-1">
        <span className="text-[11px] text-muted-foreground" title="OpenRouter 계정 전체 사용량입니다 (제품 채팅·dev·SAMS 포함).">
          OpenRouter 사용량 · 마지막 충전 이후
        </span>
        {meter == null ? (
          <span className="text-2xl font-semibold">…</span>
        ) : "error" in meter ? (
          <span className="text-xs text-muted-foreground" title={meter.error}>OpenRouter 잔액을 읽지 못했습니다.</span>
        ) : (
          <>
            <span className="text-2xl font-semibold tabular-nums">
              {dollars(meter.used_usd)} <span className="text-base font-normal text-muted-foreground">/ {dollars(meter.budget_usd)}</span>
            </span>
            <span className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
              <i
                className={cn("block h-full", ratio != null && ratio >= 0.9 ? "bg-destructive" : "bg-primary")}
                style={{ width: `${Math.min(100, (ratio ?? 0) * 100)}%` }}
              />
            </span>
            <span className={cn("text-[11px] tabular-nums", ratio != null && ratio >= 0.9 ? "text-destructive" : "text-muted-foreground")}>
              남은 금액 {dollars(meter.remaining_usd)}
              {meter.period_started_at ? ` · ${compactTime(meter.period_started_at)} 충전 이후` : " · 계정 시작 이후"}
            </span>
          </>
        )}
      </div>
    </Card>
  );
}
